/**
 * Citation extraction utilities for the Reference Sidebar
 * Extracts URLs from markdown content and converts them to numbered citations
 */

import {
  DOCUMENT_SLUG_PATTERN_SRC,
  buildDocumentPath,
  documentSlug,
} from './documentMetadata';
import { parseFileIdHref, parseStorageNodeHref } from './markdownUtils';
import { type ExtractedUrl, extractUrlsFromContent } from './urlExtractor';

/**
 * Citation interface representing a numbered reference
 */
export interface Citation {
  number: number; // Citation number (1, 2, 3, etc.)
  text: string; // Display text/title for the reference
  url?: string | undefined; // Original URL from content
  processedUrl?: string | undefined; // Pre-calculated URL ready for navigation (internal or external)
  isInternalLink?: boolean | undefined; // True if this is an internal navigation link
  context?: string | undefined; // Surrounding context from the content
  domain?: string | undefined; // Domain name for grouping
  shortUrl?: string | undefined; // Shortened URL for display
  refId?: string | undefined; // Unique reference ID for bi-directional highlighting (e.g., "ref-1")
}

// No dummy data needed - we extract real URLs from content

/**
 * Extract document_content_id from a URL
 * Handles URLs like:
 * - https://www.lexomat.sk/sk/document/12345
 * - /document/12345
 * - Pure numeric: 12345
 */
function extractDocumentContentId(url: string): string | null {
  try {
    // Try to parse as URL
    const urlObj = new URL(url);
    // Extract from pathname like /sk/dokument/12345 or /en/document/12345
    // Both slugs accepted: Linkformat emits the locale-specific slug
    // (sk → dokument, en → document) — see documentSlug() in documentMetadata.ts.
    const pathMatch = urlObj.pathname.match(
      new RegExp(`/${DOCUMENT_SLUG_PATTERN_SRC}/(\\d+)`, 'i')
    );
    if (pathMatch?.[1]) {
      return pathMatch[1];
    }
  } catch {
    // Not a valid URL, try other patterns
  }

  // Try relative path like /document/12345 or /dokument/12345
  const relativeMatch = url.match(
    new RegExp(`^/${DOCUMENT_SLUG_PATTERN_SRC}/(\\d+)`, 'i')
  );
  if (relativeMatch?.[1]) {
    return relativeMatch[1];
  }

  // Try pure numeric like 12345
  if (/^\d+$/.test(url)) {
    return url;
  }

  return null;
}

/**
 * Extract IRI path from a URL
 * Handles URLs like:
 * - https://www.lexomat.sk/sk/document/SK/ZZ/1964/40
 * - /document/SK/ZZ/1964/40
 * - /SK/ZZ/1964/40
 */
function extractIriPath(url: string): string | null {
  try {
    const urlObj = new URL(url);
    // Extract from pathname like /sk/dokument/SK/ZZ/1964/40 or /en/document/...
    // Both slugs accepted (sk → dokument, en → document).
    const pathMatch = urlObj.pathname.match(
      new RegExp(`/${DOCUMENT_SLUG_PATTERN_SRC}(\\/SK\\/[^#?]+)`, 'i')
    );
    if (pathMatch?.[1]) {
      return pathMatch[1];
    }
  } catch {
    // Not a valid URL, try other patterns
  }

  // Try relative path like /document/SK/ZZ/1964/40 or /dokument/SK/ZZ/1964/40
  const relativeMatch = url.match(
    new RegExp(`^/${DOCUMENT_SLUG_PATTERN_SRC}(\\/SK\\/[^#?]+)`, 'i')
  );
  if (relativeMatch?.[1]) {
    return relativeMatch[1];
  }

  // Try direct IRI like /SK/ZZ/1964/40
  const directMatch = url.match(/^(\/SK\/[^#?]+)/i);
  if (directMatch?.[1]) {
    return directMatch[1];
  }

  return null;
}

/**
 * Process URL for citation - pre-calculate navigation URL
 * This runs during citation extraction to avoid processing on every click
 */
export function processUrlForCitation(
  url: string,
  locale = 'sk'
): {
  processedUrl: string;
  isInternalLink: boolean;
} {
  const parsedFileId = parseFileIdHref(url);
  if (parsedFileId) {
    return {
      processedUrl: `file_id:${parsedFileId.fileId}${parsedFileId.fragment}`,
      isInternalLink: true,
    };
  }

  const parsedStorageNode = parseStorageNodeHref(url);
  if (parsedStorageNode) {
    return {
      processedUrl: `storage_node:${parsedStorageNode.nodeId}${parsedStorageNode.fragment}`,
      isInternalLink: true,
    };
  }

  // Try to extract document_content_id from the URL
  const documentContentId = extractDocumentContentId(url);

  if (documentContentId) {
    // Internal link - build the navigation URL to document page
    // Preserve the query parameters and fragment (hash) from the original URL if present
    let queryAndFragment = '';
    try {
      const urlObj = new URL(url);
      // Combine search params and hash
      queryAndFragment = urlObj.search + urlObj.hash;
    } catch {
      // If URL parsing fails, try to extract query and fragment manually
      const questionIndex = url.indexOf('?');
      const hashIndex = url.indexOf('#');

      if (questionIndex !== -1 || hashIndex !== -1) {
        // Find the start of query or fragment, whichever comes first
        const startIndex =
          questionIndex !== -1 &&
          (hashIndex === -1 || questionIndex < hashIndex)
            ? questionIndex
            : hashIndex;
        queryAndFragment = url.substring(startIndex);
      }
    }

    // TODO: Later add back chat redirect option
    // OLD: processedUrl: `/${locale}/chat/temp?id=${documentContentId}`,
    // Use localized path: /sk/dokument/... or /en/document/...
    return {
      processedUrl: `${buildDocumentPath(locale, documentContentId)}${queryAndFragment}`,
      isInternalLink: true,
    };
  }

  // Try to extract IRI path from the URL (e.g., /SK/ZZ/1964/40)
  const iriPath = extractIriPath(url);
  if (iriPath) {
    // IRI-based internal link - build the navigation URL
    // Preserve fragment (hash) from the original URL
    let fragment = '';
    try {
      const urlObj = new URL(url);
      fragment = urlObj.hash;
    } catch {
      const hashIndex = url.indexOf('#');
      if (hashIndex !== -1) {
        fragment = url.substring(hashIndex);
      }
    }

    // Build URL: /sk/dokument/SK/ZZ/1964/40#fragment or /en/document/...
    return {
      processedUrl: `/${locale}/${documentSlug(locale)}${iriPath}${fragment}`,
      isInternalLink: true,
    };
  }

  // External link - use as-is
  return {
    processedUrl: url,
    isInternalLink: false,
  };
}

/**
 * Convert ExtractedUrl to Citation with auto-numbering and pre-calculated URLs
 */
function convertUrlToCitation(
  extractedUrl: ExtractedUrl,
  index: number,
  locale = 'sk'
): Citation {
  const number = index + 1;

  // Pre-calculate the URL for navigation
  const { processedUrl, isInternalLink } = extractedUrl.url
    ? processUrlForCitation(extractedUrl.url, locale)
    : { processedUrl: undefined, isInternalLink: false };

  return {
    number, // Auto-number starting from 1
    text: extractedUrl.displayText,
    url: extractedUrl.url,
    processedUrl,
    isInternalLink,
    domain: extractedUrl.domain,
    shortUrl: extractedUrl.shortUrl,
    refId: `ref-${number}`, // Generate unique refId for bi-directional highlighting
  };
}

/**
 * Extract citations from markdown content by finding all URLs
 * and converting them to numbered citations
 * @param content - The markdown content to extract citations from
 * @param locale - The locale for internal link navigation (default: 'sk')
 */
export function extractCitationsFromContent(
  content: string,
  locale = 'sk'
): Citation[] {
  // Extract all URLs from the content
  const extractedUrls = extractUrlsFromContent(content);

  // Convert URLs to citations with auto-numbering and pre-calculated URLs
  const citations = extractedUrls.map((url, index) =>
    convertUrlToCitation(url, index, locale)
  );

  return citations;
}

/**
 * Deduplicate citations by URL and re-number
 */
export function deduplicateCitations(citations: Citation[]): Citation[] {
  const seen = new Set<string>();
  const unique: Citation[] = [];

  citations.forEach((citation) => {
    if (citation.url && !seen.has(citation.url)) {
      seen.add(citation.url);
      unique.push(citation);
    }
  });

  // Re-number after deduplication and regenerate refIds
  return unique.map((citation, index) => {
    const number = index + 1;
    return {
      ...citation,
      number,
      refId: `ref-${number}`, // Regenerate refId to match new number
    };
  });
}

/**
 * Sort citations by number
 */
export function sortCitationsByNumber(citations: Citation[]): Citation[] {
  return [...citations].sort((a, b) => a.number - b.number);
}
